import type * as React from 'react';
import { ChatDrawerHeader } from './chat-drawer-header.tsx';
import { ChatDrawerStrip } from './chat-drawer-strip.tsx';
import { CHAT_DRAWER_MAX_WIDTH, CHAT_DRAWER_MIN_WIDTH } from './chatDrawerWidth.ts';
import { containTab } from './containTab.ts';
import { followInApp } from './followInApp.ts';
import { cn } from '../../lib/cn.ts';
import { CHAT_COLUMN_CLASS } from './chatColumn.ts';
import { ResizeHandle } from '../resize-handle/resize-handle.tsx';
import { useChatDrawerLayout } from './useChatDrawerLayout.ts';

/** How far one arrow key moves the drag handle, and how far with shift held. */
const RESIZE_STEP = 16;
const RESIZE_SHIFT_STEP = 48;

export interface ChatDrawerProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  open: boolean;
  /** Shown in the header. Also names the region for screen readers. */
  title: string;
  /** The page's invitation to ask something. Left out on pages that have none. */
  subtitle?: string;
  /** The header control on a phone, where dismissing the drawer closes it. */
  closeLabel: string;
  /** The header control on a desktop, where dismissing it leaves the strip. */
  collapseLabel: string;
  /** Names the collapsed strip — the whole strip is the way back in. */
  stripLabel: string;
  /** Names the drag handle. Required alongside `onWidthChange`. */
  resizeLabel?: string;
  /** Collapse on a desktop, close on a phone. Escape asks for the same thing. */
  onDismiss: () => void;
  /** Open it again from the strip. */
  onExpand: () => void;
  /**
   * How wide it is drawn, in pixels. Ignored where the drawer covers the page.
   * Left out to open at the width the design picks for this window.
   */
  width?: number;
  /** Left out to make the drawer a fixed width. */
  onWidthChange?: (width: number) => void;
  /** Home or a double-click on the handle: back to the width nobody chose. */
  onWidthReset?: () => void;
  /** The strip is where the caret goes when the drawer collapses under it. */
  stripRef?: React.Ref<HTMLButtonElement>;
  /** Something arrived while the drawer was away. Marked on the strip. */
  unread?: boolean;
  /** Says the same thing out loud. Required alongside `unread`. */
  unreadLabel?: string;
  /** The earlier conversations. Only drawn in full screen, where there is room. */
  sidebar?: React.ReactNode;
  /** Extra header controls, left of the dismiss button — history, new, full screen. */
  action?: React.ReactNode;
  /** Covers the whole window, for a conversation worth giving the screen to. */
  expanded?: boolean;
}

/**
 * The panel Wundo is talked to in, fixed to the right edge: open, collapsed to
 * a strip, or expanded; see docs/design/app-chat.md. Collapsed it is parked
 * off-screen rather than removed, so the conversation inside survives.
 */
export function ChatDrawer({
  open,
  title,
  subtitle,
  closeLabel,
  collapseLabel,
  stripLabel,
  resizeLabel,
  onDismiss,
  onExpand,
  width,
  onWidthChange,
  onWidthReset,
  stripRef,
  unread = false,
  unreadLabel,
  sidebar,
  action,
  expanded = false,
  className,
  children,
  onKeyDown,
  ...props
}: ChatDrawerProps) {
  const layout = useChatDrawerLayout({ open, expanded, width });
  /*
   * Open on a phone, nothing else is left on screen, so the drawer is a modal
   * there: announced as one, and holding the tab order. Full screen is not: it
   * keeps the rail precisely so navigation stays usable.
   */
  const dialog = layout.belowMobile && open;

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event);

    if (event.key === 'Escape' && open) {
      onDismiss();
      return;
    }

    if (event.key === 'Tab' && dialog) {
      containTab(event);
    }
  }

  return (
    /*
     * A div rather than an <aside>: the drawer is a complementary region most
     * of the time and a dialog on a phone, and an <aside> may not be given the
     * dialog role. Saying the region role out loud costs nothing and lets the
     * one element carry both.
     */
    <div
      data-slot='chat-drawer'
      data-open={open}
      data-expanded={expanded}
      data-keyboard={layout.keyboard}
      aria-label={title}
      role={dialog ? 'dialog' : 'complementary'}
      aria-modal={dialog ? true : undefined}
      onKeyDown={handleKeyDown}
      style={layout.style}
      className={cn(
        /*
         * Below the impersonation banner, like the rail: over it, the drawer
         * would cover the banner's stop button. `--app-banner-h` is 0 without one.
         */
        'fixed top-[var(--app-banner-h,0px)] bottom-0 right-0 z-30 flex flex-col overscroll-contain touch-manipulation',
        /*
         * Width animates too: leaving full screen is a size change, not a
         * slide. `translate` must be named — the slide classes set the CSS
         * `translate` property, which a bare `transform` does not cover.
         */
        'transition-[translate,transform,width] duration-200 ease-out',
        /*
         * Sized to the visible area, the panel stops where the keyboard begins.
         * iOS draws its autofill bar there see-through, so the panel's own
         * surface runs on under it instead of the page behind.
         */
        'max-mobile:after:absolute max-mobile:after:inset-x-0 max-mobile:after:top-full max-mobile:after:h-screen max-mobile:after:bg-inherit',
        open ? 'translate-x-0' : layout.parked,
        expanded
          ? 'bg-canvas'
          : 'bg-surface border-l border-line shadow-drawer max-mobile:w-screen',
        className,
      )}
      {...props}
    >
      {open && !layout.filling && onWidthChange && resizeLabel ? (
        <ResizeHandle
          label={resizeLabel}
          value={layout.drawnWidth}
          min={CHAT_DRAWER_MIN_WIDTH}
          max={CHAT_DRAWER_MAX_WIDTH}
          defaultValue={layout.openingWidth}
          step={RESIZE_STEP}
          shiftStep={RESIZE_SHIFT_STEP}
          onChange={onWidthChange}
          onReset={onWidthReset}
        />
      ) : null}

      {layout.belowMobile ? null : (
        <ChatDrawerStrip
          open={open}
          label={stripLabel}
          onExpand={onExpand}
          stripRef={stripRef}
          unread={unread}
          unreadLabel={unreadLabel}
        />
      )}

      <div
        data-slot='chat-drawer-content'
        /* Parked, nothing in here may be tabbed to, read out, or clicked through the strip. */
        aria-hidden={!open}
        inert={!open}
        className={cn(
          'flex min-h-0 flex-1 transition-opacity duration-150',
          expanded ? 'flex-row' : 'flex-col',
          open ? 'opacity-100' : 'opacity-0',
        )}
      >
        {expanded ? sidebar : null}
        {/*
          Docked there is only one column, so this wrapper must not exist —
          `contents` takes it out of the layout and leaves the header, the body
          and the composer as the drawer's own children.
        */}
        <div className={expanded ? 'flex min-w-0 flex-1 flex-col' : 'contents'}>
          <ChatDrawerHeader
            title={title}
            subtitle={subtitle}
            expanded={expanded}
            action={action}
            belowMobile={layout.belowMobile}
            closeLabel={closeLabel}
            collapseLabel={collapseLabel}
            onDismiss={onDismiss}
          />
          {children}
        </div>
      </div>
    </div>
  );
}

export interface ChatDrawerBodyProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  /** Must match the drawer's own `expanded`. */
  expanded?: boolean;
  /**
   * Follows a link in the conversation that points at a page of this same app,
   * given the path it points at. Left out — in a story, or anywhere without a
   * router — such a link loads its page the ordinary way instead, which works
   * but costs the conversation.
   */
  onNavigate?: (path: string) => void;
}

/**
 * The scrolling middle of the drawer.
 *
 * Empty, it is empty: no greeting, no card, nothing standing in for a
 * conversation that has not started. What there is to ask sits in the pills
 * above the composer, where the hand already is.
 *
 * Expanded, the scroll still belongs to the full width — a scrollbar hugging a
 * centred column looks like a mistake — so the column is inside it.
 */
export function ChatDrawerBody({
  expanded = false,
  onNavigate,
  className,
  children,
  onClick,
  ...props
}: ChatDrawerBodyProps) {
  function handleClick(event: React.MouseEvent<HTMLDivElement>) {
    onClick?.(event);

    if (onNavigate) {
      followInApp(event, onNavigate);
    }
  }

  return (
    <div
      data-slot='chat-drawer-body'
      onClick={handleClick}
      /*
       * `min-h-0` so it actually scrolls: a flex child refuses to shrink below
       * its content by default, and the conversation would then push the
       * composer down the page instead of scrolling under it.
       */
      className={cn(
        'min-h-0 flex-1 overflow-y-auto overscroll-contain px-5.5 py-5 [overflow-anchor:none]',
        className,
      )}
      {...props}
    >
      <div className={cn(expanded && CHAT_COLUMN_CLASS)}>{children}</div>
    </div>
  );
}

export interface ChatDrawerFooterProps extends Omit<
  React.ComponentProps<'div'>,
  'style'
> {
  /** Must match the drawer's own `expanded`. */
  expanded?: boolean;
}

/** Holds the composer, and stays put while the body scrolls. */
export function ChatDrawerFooter({
  expanded = false,
  className,
  children,
  ...props
}: ChatDrawerFooterProps) {
  return (
    <div
      data-slot='chat-drawer-footer'
      /*
       * Above the home indicator — except with the keyboard up, which covers it
       * and would leave the inset as a gap under the composer.
       */
      className={cn(
        'shrink-0 border-t border-line px-4 pt-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))]',
        'in-data-[keyboard=true]:pb-3.5',
        className,
      )}
      {...props}
    >
      <div className={cn(expanded && CHAT_COLUMN_CLASS)}>{children}</div>
    </div>
  );
}

export type ChatDisclaimerProps = Omit<React.ComponentProps<'p'>, 'style'>;

export function ChatDisclaimer({ className, ...props }: ChatDisclaimerProps) {
  return (
    <p
      data-slot='chat-disclaimer'
      className={cn('mt-2 text-center text-xs text-ink-muted', className)}
      {...props}
    />
  );
}
